import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { WhatsappLogo, Copy, CheckCircle, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

const STAGES = [
  { key: "awaiting_design_prep", label: "Awaiting prep", color: "bg-[var(--pf-yellow)]" },
  { key: "design_prepared", label: "Design prepared", color: "bg-[var(--pf-cyan)] text-black" },
  { key: "in_production", label: "In production", color: "bg-[var(--pf-brand)] text-white" },
  { key: "quality_check", label: "Quality check", color: "bg-[var(--pf-brand)] text-white" },
  { key: "dispatched", label: "Dispatched", color: "bg-[var(--pf-text)] text-white" },
  { key: "delivered", label: "Delivered", color: "bg-[#22C55E] text-white" },
  { key: "cancelled", label: "Cancelled", color: "bg-[var(--pf-surface-2)]" },
];

export default function AdminWhatsApp() {
  const [config, setConfig] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [copied, setCopied] = useState(false);
  const [previewDesignId, setPreviewDesignId] = useState(null);

  const load = () => {
    Promise.all([
      api.get("/whatsapp/config"),
      api.get("/whatsapp/sessions"),
      api.get("/whatsapp/orders"),
    ]).then(([c, s, o]) => {
      setConfig(c.data);
      setSessions(s.data);
      setOrders(o.data);
    }).catch(() => toast.error("Failed to load WhatsApp data"));
  };

  useEffect(() => { load(); }, []);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const updateOrder = async (orderId, stage) => {
    try {
      await api.patch(`/whatsapp/orders/${orderId}`, {
        fulfillment_status: stage,
        notify_customer: true,
      });
      toast.success(`Marked as ${stage.replace(/_/g, " ")}. Customer notified via WhatsApp.`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const shareText = config
    ? `Order print material via WhatsApp!\n\n1. Save this number: ${config.sandbox_number}\n2. Send this exact message: "${config.join_code}"\n3. Then chat with our AI bot to design & order.`
    : "";

  const awaitingPrep = orders.filter(o => (o.fulfillment_status || "awaiting_design_prep") === "awaiting_design_prep");
  const inProgress = orders.filter(o => ["design_prepared","in_production","quality_check","dispatched"].includes(o.fulfillment_status));
  const completed = orders.filter(o => ["delivered","cancelled"].includes(o.fulfillment_status));

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="pf-label mb-3">Admin · WhatsApp Channel</div>
        <h1 className="text-5xl font-black tracking-tighter mb-10 flex items-center gap-4">
          <WhatsappLogo weight="fill" className="text-[#25D366]" size={48} /> WhatsApp Bot
        </h1>

        {/* Sandbox Setup */}
        {config && (
          <div className="border-2 border-[var(--pf-text)] p-6 mb-10 bg-white" data-testid="wa-sandbox-card">
            <div className="pf-label mb-3">Sandbox Onboarding · Share with customers</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="border border-[var(--pf-border)] p-4">
                <div className="pf-label mb-1">1 · Save number</div>
                <div className="font-mono font-bold text-lg">{config.sandbox_number}</div>
              </div>
              <div className="border border-[var(--pf-border)] p-4">
                <div className="pf-label mb-1">2 · First message</div>
                <div className="font-mono font-bold text-lg">"{config.join_code}"</div>
              </div>
              <div className="border border-[var(--pf-border)] p-4">
                <div className="pf-label mb-1">3 · Then send</div>
                <div className="font-mono font-bold text-lg">"hi"</div>
              </div>
            </div>
            <button onClick={() => copy(shareText)} className="pf-btn-primary" data-testid="wa-copy-share">
              {copied ? <><CheckCircle size={14}/> Copied</> : <><Copy size={14}/> Copy share message</>}
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-[var(--pf-border)] mb-10">
          {[
            ["Conversations", sessions.length],
            ["Approvals to prep", awaitingPrep.length],
            ["In progress", inProgress.length],
            ["Revenue", `₹${orders.reduce((s,o)=>s+(o.total||0),0).toLocaleString()}`],
          ].map(([l,v]) => (
            <div key={l} className="border-r border-b border-[var(--pf-border)] p-6" data-testid={`wa-stat-${l.toLowerCase().replace(/\s/g,'-')}`}>
              <div className="pf-label">{l}</div>
              <div className="font-mono text-3xl font-black mt-2">{v}</div>
            </div>
          ))}
        </div>

        {/* Approvals awaiting prep — the concierge queue */}
        <section className="mb-12">
          <div className="pf-label mb-2">Section · Concierge Queue</div>
          <h2 className="text-3xl font-black tracking-tight mb-2">Approvals awaiting design prep</h2>
          <p className="text-[var(--pf-text-2)] mb-6 max-w-2xl">
            These customers approved an AI concept. Prepare the print-ready file (300 DPI, CMYK, bleed) then mark as "Design prepared" — the customer gets a WhatsApp update automatically.
          </p>
          {awaitingPrep.length === 0 ? (
            <div className="border border-dashed border-[var(--pf-border)] p-12 text-center text-[var(--pf-text-2)]" data-testid="no-prep">
              🎉 All caught up. No approvals awaiting design prep.
            </div>
          ) : (
            <div className="border-2 border-[var(--pf-brand)]">
              {awaitingPrep.map(o => (
                <OrderRow key={o.id} o={o} onUpdate={updateOrder} onPreview={setPreviewDesignId} highlight />
              ))}
            </div>
          )}
        </section>

        {/* In progress */}
        <section className="mb-12">
          <div className="pf-label mb-2">Section · Fulfillment</div>
          <h2 className="text-3xl font-black tracking-tight mb-6">In progress</h2>
          {inProgress.length === 0 ? (
            <div className="border border-dashed border-[var(--pf-border)] p-12 text-center text-[var(--pf-text-2)]">
              Nothing in progress.
            </div>
          ) : (
            <div className="border border-[var(--pf-border)]">
              {inProgress.map(o => (
                <OrderRow key={o.id} o={o} onUpdate={updateOrder} onPreview={setPreviewDesignId} />
              ))}
            </div>
          )}
        </section>

        {/* Completed */}
        {completed.length > 0 && (
          <section className="mb-12">
            <div className="pf-label mb-2">Section · Archive</div>
            <h2 className="text-3xl font-black tracking-tight mb-6">Completed</h2>
            <div className="border border-[var(--pf-border)]">
              {completed.map(o => (
                <OrderRow key={o.id} o={o} onUpdate={updateOrder} onPreview={setPreviewDesignId} />
              ))}
            </div>
          </section>
        )}

        {/* Conversations */}
        <section>
          <div className="pf-label mb-2">Section · Live conversations</div>
          <h2 className="text-3xl font-black tracking-tight mb-6">Active chats</h2>
          {sessions.length === 0 ? (
            <div className="border border-dashed border-[var(--pf-border)] p-12 text-center text-[var(--pf-text-2)]" data-testid="no-wa-sessions">
              No WhatsApp conversations yet.
            </div>
          ) : (
            <div className="border border-[var(--pf-border)]">
              <div className="grid grid-cols-12 gap-4 p-4 border-b border-[var(--pf-border)] pf-label bg-white">
                <div className="col-span-3">Phone</div>
                <div className="col-span-3">State</div>
                <div className="col-span-4">Brief so far</div>
                <div className="col-span-2 text-right">Updated</div>
              </div>
              {sessions.map(s => (
                <div key={s.phone} className="grid grid-cols-12 gap-4 items-center p-4 border-b last:border-0 border-[var(--pf-border)]" data-testid={`wa-session-${s.phone}`}>
                  <div className="col-span-3 font-mono text-sm">{s.phone}</div>
                  <div className="col-span-3">
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 bg-[var(--pf-surface-2)]">{s.state.replace(/_/g,' ')}</span>
                  </div>
                  <div className="col-span-4 text-xs text-[var(--pf-text-2)]">
                    {s.brief?.product_label ? `${s.brief.product_label} · ${s.brief.industry || '—'} · ${s.brief.quantity || '—'}` : '—'}
                  </div>
                  <div className="col-span-2 text-right pf-label">{new Date(s.updated_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Design preview modal */}
      {previewDesignId && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" onClick={()=>setPreviewDesignId(null)}>
          <div className="bg-white border-2 border-[var(--pf-text)] max-w-3xl w-full p-4" onClick={(e)=>e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div className="pf-label">Design · {previewDesignId}</div>
              <button onClick={()=>setPreviewDesignId(null)} className="pf-label hover:text-[var(--pf-brand)]">Close ✕</button>
            </div>
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/wa-media/${previewDesignId}.png`} alt="design" className="w-full" />
          </div>
        </div>
      )}
    </div>
  );
}


function OrderRow({ o, onUpdate, onPreview, highlight }) {
  const current = o.fulfillment_status || "awaiting_design_prep";
  const stage = STAGES.find(s => s.key === current) || STAGES[0];
  return (
    <div className={`grid grid-cols-12 gap-4 items-center p-4 border-b last:border-0 border-[var(--pf-border)] ${highlight ? 'bg-[var(--pf-brand)]/5' : ''}`} data-testid={`wa-order-${o.id}`}>
      <div className="col-span-3">
        <div className="font-mono text-xs text-[var(--pf-text-2)]">#{o.id}</div>
        <div className="font-mono text-sm mt-1">{o.phone}</div>
        <div className="text-xs text-[var(--pf-text-2)] mt-1">{new Date(o.created_at).toLocaleString([], {month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}</div>
      </div>
      <div className="col-span-3">
        <div className="font-semibold">{o.brief?.product_label} × {o.brief?.quantity?.toLocaleString()}</div>
        <div className="text-xs text-[var(--pf-text-2)] mt-1">{o.brief?.industry}</div>
      </div>
      <div className="col-span-2">
        <div className="font-mono font-semibold">₹{o.total?.toLocaleString()}</div>
        <div className="text-xs text-[var(--pf-text-2)] mt-1">incl. GST</div>
      </div>
      <div className="col-span-1">
        {o.design_id && (
          <button onClick={()=>onPreview(o.design_id)} className="p-2 hover:bg-white border border-[var(--pf-border)]" title="Preview design" data-testid={`preview-${o.id}`}>
            <Eye size={16} />
          </button>
        )}
      </div>
      <div className="col-span-3">
        <select value={current} onChange={(e)=>onUpdate(o.id, e.target.value)} data-testid={`stage-${o.id}`}
          className="border-2 border-[var(--pf-border)] px-2 py-2 text-[10px] uppercase tracking-widest font-bold bg-white w-full">
          {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <span className={`inline-block mt-1 text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 ${stage.color}`}>{stage.label}</span>
      </div>
    </div>
  );
}
